export const GAME_WIDTH = 540;
export const GAME_HEIGHT = 960;
export const PLAYER_Y = 844;
export const DANGER_LINE_Y = 865;
export const PLAYER_SPEED = 430;
export const FIRE_INTERVAL_MS = 470;
export const POST_HIT_PROTECTION_MS = 1_600;
export const STARTING_LIVES = 3;
export const MAX_LIVES = 4;
export const EXTRA_LIFE_CLOGS = 25;
export const BUBBLE_WEAPON_CLOGS = 10;
export const PLUNGER_WEAPON_CLOGS = 25;

export type EnemyKind = "regular" | "speedy" | "sturdy" | "boss";
export type PowerUpKind = "triple" | "shield";
export type WeaponKind = "poop" | "bubble" | "plunger";

export interface WeaponDefinition {
  readonly kind: WeaponKind;
  readonly label: string;
  readonly texture: string;
  readonly fireInterval: number;
  readonly speed: number;
  readonly damage: number;
  readonly width: number;
  readonly height: number;
}

export const WEAPONS: Record<WeaponKind, WeaponDefinition> = {
  poop: {
    kind: "poop",
    label: "Poop Popper",
    texture: "pellet-art",
    fireInterval: FIRE_INTERVAL_MS,
    speed: 620,
    damage: 1,
    width: 30,
    height: 30,
  },
  bubble: {
    kind: "bubble",
    label: "Bubble Blaster",
    texture: "shot-bubble",
    fireInterval: 360,
    speed: 690,
    damage: 1,
    width: 36,
    height: 36,
  },
  plunger: {
    kind: "plunger",
    label: "Golden Plunger Cannon",
    texture: "shot-plunger",
    fireInterval: 410,
    speed: 720,
    damage: 2,
    width: 30,
    height: 44,
  },
};

export interface EnemyDefinition {
  readonly kind: EnemyKind;
  readonly label: string;
  readonly hitPoints: number;
  readonly score: number;
  readonly speed: number;
  readonly width: number;
  readonly height: number;
}

export const ENEMIES: Record<EnemyKind, EnemyDefinition> = {
  regular: {
    kind: "regular",
    label: "Regular Toilet",
    hitPoints: 3,
    score: 100,
    speed: 46,
    width: 82,
    height: 123,
  },
  speedy: {
    kind: "speedy",
    label: "Speedy Potty",
    hitPoints: 2,
    score: 150,
    speed: 78,
    width: 70,
    height: 105,
  },
  sturdy: {
    kind: "sturdy",
    label: "Sturdy Throne",
    hitPoints: 5,
    score: 250,
    speed: 35,
    width: 100,
    height: 150,
  },
  boss: {
    kind: "boss",
    label: "Lid Shield",
    hitPoints: 24,
    score: 2_000,
    speed: 0,
    width: 226,
    height: 234,
  },
};

export interface SpawnPlan {
  readonly at: number;
  readonly kind: Exclude<EnemyKind, "boss">;
  readonly lane: number;
  readonly motion: "straight" | "diagonal" | "sway";
}

const lane = (index: number): number =>
  [92, 181, 270, 359, 448][index % 5] ?? 270;

export const SPAWN_PLAN: readonly SpawnPlan[] = [
  ...Array.from({ length: 9 }, (_, index) => ({
    at: 5_000 + index * 2_650,
    kind: "regular" as const,
    lane: lane(index * 2),
    motion: "straight" as const,
  })),
  ...Array.from({ length: 11 }, (_, index) => ({
    at: 29_500 + index * 2_550,
    kind: index % 4 === 3 ? ("speedy" as const) : ("regular" as const),
    lane: lane(index * 3 + 1),
    motion: index % 2 === 0 ? ("diagonal" as const) : ("straight" as const),
  })),
  ...Array.from({ length: 14 }, (_, index) => ({
    at: 60_000 + index * 2_450,
    kind:
      index % 5 === 0
        ? ("sturdy" as const)
        : index % 3 === 0
          ? ("speedy" as const)
          : ("regular" as const),
    lane: lane(index * 2 + 2),
    motion:
      index % 3 === 0
        ? ("sway" as const)
        : index % 2 === 0
          ? ("diagonal" as const)
          : ("straight" as const),
  })),
];

export const BOSS_START_MS = 98_000;
export const BOSS_WARNING_START_MS = BOSS_START_MS - 4_000;
export const TRIPLE_DURATION_MS = 8_000;
